/** React Query cache keys, in one place so invalidation after a write is easy to get right. */

import type { EnquiryStatus, ItemGroup, EnquiryType } from '../types'

export interface ItemQuery {
  enquiry_type?: EnquiryType
  group?: ItemGroup
  q?: string
  /** Exact-match batch lookup; overrides the other filters (contract §4). */
  codes?: string[]
  limit?: number
  offset?: number
}

export interface EnquiryQuery {
  status?: EnquiryStatus
  purchaser?: string
  sales_person?: string
  awaiting_gt_days?: number
  limit?: number
  offset?: number
}

/** `"YYYY-MM-DD"`, both ends optional — `analytics.get_pipeline_analytics?from_date=&to_date=`. */
export interface PipelineRange {
  from_date?: string
  to_date?: string
}

export const qk = {
  items: (query: ItemQuery = {}) => ['items', query] as const,
  item: (code: string) => ['items', code] as const,
  suppliers: (q?: string, company?: string) => ['suppliers', q ?? '', company ?? ''] as const,
  /** Bosun users, optionally filtered by role. `''` for the unfiltered list so the key is
   *  stable — `undefined` and `''` must not be two cache entries for the same request. */
  users: (role?: string) => ['users', role ?? ''] as const,
  purchaseHistory: (item: string, months = 2) => ['purchase-history', item, months] as const,

  enquiries: (query: EnquiryQuery = {}) => ['enquiries', query] as const,
  /** Board-wide supplier-reply state (`useBoardReplies`), keyed by the SORTED enquiry ids it covers
   *  — a board that merely reorders must not be a different cache entry. */
  boardReplies: (enquiryIds: readonly string[]) => ['board-replies', enquiryIds] as const,
  enquiry: (id: string) => ['enquiries', id] as const,
  rfqs: (id: string) => ['enquiries', id, 'rfqs'] as const,
  supplierQuotations: (id: string) => ['enquiries', id, 'supplier-quotations'] as const,
  supplierQuotation: (name: string) => ['supplier-quotations', name] as const,
  comparison: (id: string) => ['enquiries', id, 'comparison'] as const,

  quotation: (id: string) => ['quotations', id] as const,
  /** Keyed by ENQUIRY, not the quotation's own id: the name is resolved by a `Quotation`
   * doctype lookup filtered on `enquiry` (`findQuotationName`, a plain GET, no write), then
   * loaded via `quotation.get_quotation` (Task 11). `comparison.build_quotation`, the get-or-
   * create this used to route through, is unusable — see `useQuotation.ts`'s own header. */
  quotationForEnquiry: (enquiryId: string) => ['enquiries', enquiryId, 'quotation'] as const,
  /** A SEPARATE cache entry from `quotationForEnquiry` even though it resolves the same
   * document: `useExistingQuotation`'s queryFn swallows a "nothing built yet" failure to `null`
   * where `useQuotation`'s throws, and two hooks with different success/error shapes must not
   * share one cached query. */
  existingQuotationForEnquiry: (enquiryId: string) => ['enquiries', enquiryId, 'quotation', 'existing'] as const,

  /**
   * A quotation's edit history (Frappe `Version` rows) — keyed by the QUOTATION's own name, not
   * the enquiry's, because that is what `Version.docname` holds.
   *
   * Every mutation that writes to the quotation must invalidate this. Frappe records a `Version`
   * for each save, so a stale history shows an edit as never having happened — which is worse
   * than showing no history at all, since the panel's whole claim is that it is the server's
   * record.
   */
  quotationHistory: (quotationName: string) => ['quotation-history', quotationName] as const,

  /** Every time a quotation was emailed (`Email Queue`). Invalidated by a send, like the history. */
  quotationSends: (quotationName: string) => ['quotation-sends', quotationName] as const,
  revisions: (quotationId: string) => ['quotations', quotationId, 'revisions'] as const,
  quotedItems: (customer: string) => ['customers', customer, 'quoted-items'] as const,

  /**
   * `mail.get_emails` (Task 6, on the `quoflow.quoflow.quoflow.api.mail` module) takes a REAL
   * `folder` param and pages server-side, unlike the old `get_email_communications` this key once
   * backed — so the key now carries both, and a folder switch or a limit change is a genuinely
   * different cached request rather than a `select` over one shared whole-mailbox fetch.
   */
  mailbox: (folder: string, limit = 200) => ['mail', 'mailbox', folder, limit] as const,
  mailMessage: (id: string) => ['mail', 'message', id] as const,
  mailboxInfo: () => ['mail', 'mailbox-info'] as const,

  /** `settings.get_pricing_rules` (Task 11) bundles category margins and the default markup —
   * see `PricingRulesSettings`'s own comment — so there is one settings cache entry now, not
   * three. */
  settingsPricingRules: () => ['settings', 'pricing-rules'] as const,

  /** `analytics.get_pipeline_analytics?from_date=&to_date=` (Task 11, live-verified) takes a
   * date range, not a "mine/team" scope — the old key shape had no live endpoint behind it. */
  analyticsPipeline: (range: PipelineRange = {}) => ['analytics', 'pipeline', range] as const,

  /**
   * Root invalidation targets — matches any key under it (React Query's default
   * `invalidateQueries` is a prefix match), rather than one leaf. `useRealtime.ts` invalidates
   * by ROOT, not by a scoped id, because the realtime event payloads are unconfirmed (see that
   * file's header) — only the event NAMES are live-verified. `useMailMutation` (`useMail.ts`)
   * uses the same root after a write, for the same "touch everything under this domain" reason.
   */
  mailRoot: () => ['mail'] as const,
  /** Covers `enquiries`, `enquiry`, `rfqs`, `supplierQuotations`, `comparison`,
   * `quotationForEnquiry` and `existingQuotationForEnquiry` above — every one of them nests
   * under `['enquiries', …]`. Does NOT cover `revisions`, despite the name suggesting otherwise:
   * `revisions(quotationId)` is `['quotations', quotationId, 'revisions']`, prefixed by
   * `'quotations'`, not `'enquiries'` — invalidating this root refreshes nothing there. If a
   * future realtime event needs to touch revisions, invalidate `['quotations']` (or that key)
   * explicitly; do not assume this root reaches it. */
  enquiriesRoot: () => ['enquiries'] as const,
  /** `supplierQuotation(name)` above is keyed by SQ name, not enquiry id, so it sits outside
   * `enquiriesRoot` and needs its own root. Currently unused by `useRealtime.ts` on purpose: on
   * `sq.received`, only the per-enquiry LIST (`supplierQuotations`, under `enquiriesRoot`) is
   * refreshed — the per-SQ-name detail cache is deliberately left alone, since the event carries
   * no confirmed field to say which single `supplierQuotation(name)` entry (if any is even
   * cached) it corresponds to. Exists so a future scoped invalidation has a root ready. */
  supplierQuotationsRoot: () => ['supplier-quotations'] as const,
}
